import React, { useState, useCallback, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, Mail, Lock, User, Store, ShoppingBag, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { useAuth } from '@/lib/auth-context';
import { loadLoginPrefs, saveLoginPrefs } from '@/lib/auth-storage';
import { toast } from 'sonner';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { buildPathForLocale, localizeInternalPath } from '@/lib/intl-seo';

const LoginPage = () => {
  const { t, i18n } = useTranslation();
  const { login, register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get('redirect');
  const tabParam = searchParams.get('tab');
  const registerParam = searchParams.get('register');
  const { executeRecaptcha } = useGoogleReCaptcha();
  const lc = (i18n.language || 'en').substring(0, 2);
  const locPath = (segments) => buildPathForLocale(lc, segments);

  const [isRegister, setIsRegister] = useState(registerParam === 'true');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [activeTab, setActiveTab] = useState(tabParam === 'seller' ? 'seller' : 'buyer');
  const [buyerForm, setBuyerForm] = useState({
    email: '',
    password: '',
    name: '',
    role: 'buyer'
  });
  const [sellerForm, setSellerForm] = useState({
    email: '',
    password: '',
    name: '',
    role: 'seller'
  });
  const [buyerRememberMe, setBuyerRememberMe] = useState(true);
  const [sellerRememberMe, setSellerRememberMe] = useState(true);

  useEffect(() => {
    const buyerPrefs = loadLoginPrefs('buyer');
    setBuyerRememberMe(buyerPrefs.rememberMe);
    if (buyerPrefs.email) {
      setBuyerForm((prev) => ({ ...prev, email: buyerPrefs.email }));
    }

    const sellerPrefs = loadLoginPrefs('seller');
    setSellerRememberMe(sellerPrefs.rememberMe);
    if (sellerPrefs.email) {
      setSellerForm((prev) => ({ ...prev, email: sellerPrefs.email }));
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage(''); // Clear previous errors

    const formData = activeTab === 'buyer' ? buyerForm : sellerForm;

    try {
      // Get reCAPTCHA token for registration
      let recaptchaToken = null;
      if (isRegister && executeRecaptcha) {
        try {
          recaptchaToken = await executeRecaptcha('register');
        } catch (recaptchaError) {
          console.error('reCAPTCHA error:', recaptchaError);
          toast.error(t('auth.recaptchaFailed'));
          setLoading(false);
          return;
        }
      }

      if (isRegister) {
        const result = await register({ ...formData, recaptcha_token: recaptchaToken });
        // Redirect to verification page
        navigate(locPath(['registration-success']), { state: { email: formData.email } });
        return;
      } else {
        const rememberMe = activeTab === 'seller' ? sellerRememberMe : buyerRememberMe;
        await login(formData.email, formData.password, { rememberMe });
        saveLoginPrefs(activeTab, formData.email, rememberMe);
        toast.success(t('auth.welcomeBack') || 'Welcome back!');
      }
      
      // Redirect based on role
      if (redirect) {
        navigate(localizeInternalPath(redirect, lc));
      } else if (formData.role === 'seller') {
        navigate(locPath(['seller']));
      } else {
        navigate(locPath(['dashboard']));
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || t('auth.authFailed') || 'Authentication failed';
      const statusCode = error.response?.status;
      
      // Check for specific error types
      if (statusCode === 403) {
        // Email not verified or seller not approved
        if (errorMsg.includes('doğrulanmamış') || errorMsg.includes('not verified')) {
          toast.error(t('auth.emailNotVerified') || 'Email adresiniz doğrulanmamış');
          navigate(locPath(['registration-success']), { state: { email: formData.email } });
        } else if (errorMsg.includes('onaylanmadı') || errorMsg.includes('not approved')) {
          toast.error(errorMsg);
          navigate(locPath(['seller-registration-pending']), { state: { email: formData.email } });
        } else {
          setErrorMessage(errorMsg);
          toast.error(errorMsg);
        }
      } else if (errorMsg.includes('doğrulanmamış') || errorMsg.includes('not verified')) {
        toast.error(t('auth.emailNotVerified') || 'Email adresiniz doğrulanmamış');
        navigate(locPath(['registration-success']), { state: { email: formData.email } });
      } else {
        setErrorMessage(errorMsg);
        toast.error(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const currentForm = activeTab === 'buyer' ? buyerForm : sellerForm;
  const setCurrentForm = activeTab === 'buyer' ? setBuyerForm : setSellerForm;

  return (
    <div className="min-h-[calc(100vh-4rem)] flex" data-testid="login-page">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-md space-y-8">
          <div className="text-center">
            <Link to="/" className="inline-flex items-center gap-2 mb-6">
              <img src="/logo.png" alt="Turkish E-Market" className="h-10 w-auto" />
              <span className="text-2xl font-serif font-bold text-primary">Turkish E-Market</span>
            </Link>
            <h1 className="text-3xl font-serif font-bold">
              {isRegister ? t('auth.createAccount') : t('auth.welcomeBack')}
            </h1>
            <p className="text-muted-foreground mt-2">
              {isRegister ? t('auth.startJourney') : t('auth.signInContinue')}
            </p>
          </div>

          {/* Buyer/Seller Tabs */}
          <Tabs value={activeTab} onValueChange={(val) => { setActiveTab(val); setErrorMessage(''); }} className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="buyer" className="flex items-center gap-2" data-testid="tab-buyer">
                <ShoppingBag className="w-4 h-4" />
                {t('auth.buyer')}
              </TabsTrigger>
              <TabsTrigger value="seller" className="flex items-center gap-2" data-testid="tab-seller">
                <Store className="w-4 h-4" />
                {t('auth.seller')}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="buyer" className="space-y-4 mt-6">
              {/* Google OAuth for Buyer */}
              <Button
                variant="outline"
                className="w-full rounded-full py-6"
                onClick={loginWithGoogle}
                data-testid="google-login-btn"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                {t('auth.continueWithGoogle')}
              </Button>

              <div className="relative">
                <Separator />
                <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-background px-2 text-xs text-muted-foreground">
                  {t('auth.or')}
                </span>
              </div>

              {/* Buyer Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                {isRegister && (
                  <div>
                    <Label htmlFor="buyer-name">{t('auth.name')}</Label>
                    <div className="relative mt-1">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input
                        id="buyer-name"
                        type="text"
                        placeholder="John Doe"
                        value={buyerForm.name}
                        onChange={(e) => setBuyerForm({ ...buyerForm, name: e.target.value })}
                        className="pl-10"
                        required
                        data-testid="input-buyer-name"
                      />
                    </div>
                  </div>
                )}

                <div>
                  <Label htmlFor="buyer-email">{t('auth.email')}</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      id="buyer-email"
                      type="email"
                      placeholder="you@example.com"
                      value={buyerForm.email}
                      onChange={(e) => setBuyerForm({ ...buyerForm, email: e.target.value })}
                      className="pl-10"
                      required
                      data-testid="input-buyer-email"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="buyer-password">{t('auth.password')}</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      id="buyer-password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={buyerForm.password}
                      onChange={(e) => setBuyerForm({ ...buyerForm, password: e.target.value })}
                      className="pl-10 pr-10"
                      required
                      minLength={6}
                      data-testid="input-buyer-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                {!isRegister && (
                  <div className="flex items-center justify-between gap-3">
                    <label
                      htmlFor="buyer-remember-me"
                      className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer select-none"
                    >
                      <Checkbox
                        id="buyer-remember-me"
                        checked={buyerRememberMe}
                        onCheckedChange={(v) => setBuyerRememberMe(v === true)}
                        data-testid="buyer-remember-me"
                      />
                      {t('auth.rememberMe')}
                    </label>
                    <Link
                      to="/forgot-password"
                      className="text-sm text-primary hover:underline shrink-0"
                      data-testid="forgot-password-link"
                    >
                      {t('auth.forgotPassword')}
                    </Link>
                  </div>
                )}

                {/* Error Message */}
                {errorMessage && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm flex items-center gap-2" data-testid="login-error-message">
                    <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    {errorMessage}
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full rounded-full py-6"
                  disabled={loading}
                  data-testid="submit-buyer-btn"
                >
                  {loading ? (t('common.loading') || 'Please wait...') : isRegister ? (t('common.register') || 'Create Account') : t('auth.signIn')}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="seller" className="space-y-4 mt-6">
              {/* Seller Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                {isRegister && (
                  <div>
                    <Label htmlFor="seller-name">{t('auth.name')}</Label>
                    <div className="relative mt-1">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input
                        id="seller-name"
                        type="text"
                        placeholder="Store Name"
                        value={sellerForm.name}
                        onChange={(e) => setSellerForm({ ...sellerForm, name: e.target.value })}
                        className="pl-10"
                        required
                        data-testid="input-seller-name"
                      />
                    </div>
                  </div>
                )}

                <div>
                  <Label htmlFor="seller-email">{t('auth.email')}</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      id="seller-email"
                      type="email"
                      placeholder="seller@example.com"
                      value={sellerForm.email}
                      onChange={(e) => setSellerForm({ ...sellerForm, email: e.target.value })}
                      className="pl-10"
                      required
                      data-testid="input-seller-email"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="seller-password">{t('auth.password')}</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      id="seller-password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={sellerForm.password}
                      onChange={(e) => setSellerForm({ ...sellerForm, password: e.target.value })}
                      className="pl-10 pr-10"
                      required
                      minLength={6}
                      data-testid="input-seller-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                {!isRegister && (
                  <div className="flex items-center justify-between gap-3">
                    <label
                      htmlFor="seller-remember-me"
                      className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer select-none"
                    >
                      <Checkbox
                        id="seller-remember-me"
                        checked={sellerRememberMe}
                        onCheckedChange={(v) => setSellerRememberMe(v === true)}
                        data-testid="seller-remember-me"
                      />
                      {t('auth.rememberMe')}
                    </label>
                    <Link
                      to="/forgot-password"
                      className="text-sm text-primary hover:underline shrink-0"
                    >
                      {t('auth.forgotPassword')}
                    </Link>
                  </div>
                )}

                {isRegister && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-800">
                      <Store className="w-4 h-4 inline mr-2" />
                      {t('auth.sellerInfo') || 'As a seller, you can list products and manage your store.'}
                    </p>
                  </div>
                )}

                {/* Error Message */}
                {errorMessage && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm flex items-center gap-2" data-testid="seller-login-error-message">
                    <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    {errorMessage}
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full rounded-full py-6"
                  disabled={loading}
                  data-testid="submit-seller-btn"
                >
                  {loading ? (t('common.loading') || 'Please wait...') : isRegister ? (t('auth.createSellerAccount') || 'Create Seller Account') : t('auth.signIn')}
                </Button>
              </form>
            </TabsContent>
          </Tabs>

          <p className="text-center text-sm text-muted-foreground">
            {isRegister ? t('auth.alreadyHaveAccount') : t('auth.dontHaveAccount')}{' '}
            <button
              type="button"
              onClick={() => setIsRegister(!isRegister)}
              className="text-primary font-medium hover:underline"
              data-testid="toggle-auth-mode"
            >
              {isRegister ? t('auth.signIn') : t('common.register')}
            </button>
          </p>
        </div>
      </div>

      {/* Right side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/90 to-primary/70" />
        <img
          src="https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=1200"
          alt="Shopping"
          className="w-full h-full object-cover mix-blend-overlay"
        />
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="text-center text-white">
            <h2 className="text-4xl font-serif font-bold mb-4">{t('auth.shopWithConfidence')}</h2>
            <p className="text-lg text-white/80 max-w-md">
              {t('auth.shopDescription')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
